import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  getVendorDirectoryCandidate,
  acceptVendorDirectoryCandidate,
  rejectVendorDirectoryCandidate,
  deferVendorDirectoryCandidate,
  restoreVendorDirectoryCandidate,
} from "../../api/clientVendorDirectoryImportApi";
import { getVendorDirectoryTaxonomy } from "../../api/clientVendorDirectoryApi";
import {
  normalizeVendorDirectoryCandidate,
  scanVendorDirectoryCandidateDuplicates,
  listVendorDirectoryDuplicateMatches,
} from "../../api/clientVendorDirectoryDuplicateApi";
import VendorCandidatePublicProfilePanel from "../../components/casamapa/admin/VendorCandidatePublicProfilePanel";
import VendorCandidateReviewContextPanels from "../../components/casamapa/admin/VendorCandidateReviewContextPanels";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatStatusLabel } from "../../utils/clientPortalFormat";
import {
  evaluateCandidateAcceptReadiness,
  resolveCandidateCategoryKey,
  resolveCandidateMunicipalityId,
} from "../../utils/vendorDirectory/candidateAcceptReadiness";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.candidates";

const REJECT_REASONS = [
  "not_a_business",
  "outside_service_area",
  "unsupported_category",
  "insufficient_information",
  "spam",
  "other",
];

const DEFER_REASONS = [
  "category_unclear",
  "municipality_unclear",
  "contact_verification_needed",
  "possible_duplicate",
];

function formatCandidateDate(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function translateKey(t, prefix, value) {
  if (!value) return "—";
  const key = `${prefix}.${value}`;
  const translated = t(key);
  return translated !== key ? translated : formatStatusLabel(value);
}

function candidateStatusBadgeClass(status) {
  if (status === "accepted") return "cvd-badge--published";
  if (status === "rejected") return "cvd-badge--suspended";
  if (["possible_duplicate", "deferred", "ready_for_review"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function StatusAlert({ variant, children }) {
  return <div className={`cvd-candidate-review__alert cvd-candidate-review__alert--${variant}`}>{children}</div>;
}

export default function ClientVendorDirectoryCandidateDetailPage() {
  const { clientViewId, candidateId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const navigate = useNavigate();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [candidate, setCandidate] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rejectReason, setRejectReason] = useState("insufficient_information");
  const [deferReason, setDeferReason] = useState("category_unclear");
  const [acceptCategoryKey, setAcceptCategoryKey] = useState("");
  const [acceptMunicipalityId, setAcceptMunicipalityId] = useState("");

  const basePath = `${serviciosBase}/candidatos`;
  const detailPrefix = `${COPY_PREFIX}.detail`;

  const categoryByKey = useMemo(() => {
    const map = new Map();
    (taxonomy?.categories || []).forEach((category) => {
      if (category.key) map.set(category.key, category);
    });
    return map;
  }, [taxonomy]);

  const municipalityById = useMemo(() => {
    const map = new Map();
    (taxonomy?.municipalities || []).forEach((municipality) => {
      if (municipality.id) map.set(municipality.id, municipality);
    });
    return map;
  }, [taxonomy]);

  const categories = useMemo(
    () => (taxonomy?.categories || []).filter((category) => category.isActive !== false),
    [taxonomy]
  );
  const municipalities = useMemo(() => taxonomy?.municipalities || [], [taxonomy]);

  useEffect(() => {
    if (!candidate) return;
    setAcceptCategoryKey((current) =>
      current || resolveCandidateCategoryKey(candidate, categories, candidate.suggestedCategoryKey)
    );
    setAcceptMunicipalityId((current) =>
      current || resolveCandidateMunicipalityId(candidate, municipalities, candidate.suggestedMunicipalityId)
    );
  }, [candidate, categories, municipalities]);

  const loadDuplicateMatches = useCallback(async () => {
    try {
      const body = await listVendorDirectoryDuplicateMatches(clientViewId, {
        candidateId,
        pageSize: 25,
      });
      const matches = (body.matches || []).filter(
        (match) =>
          !(match.candidateId && match.candidateId === match.candidateIdB) &&
          !(match.vendorId && match.vendorId === match.vendorIdB)
      );
      setDuplicateMatches(matches);
    } catch {
      setDuplicateMatches([]);
    }
  }, [candidateId, clientViewId]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [candidateBody, taxonomyBody] = await Promise.all([
        getVendorDirectoryCandidate(clientViewId, candidateId),
        getVendorDirectoryTaxonomy(clientViewId, { locale }),
      ]);
      setCandidate(candidateBody.candidate);
      setTaxonomy(taxonomyBody.taxonomy || taxonomyBody);
      await loadDuplicateMatches();
    } catch (error) {
      setErrorMessage(error.message || t(`${detailPrefix}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, candidateId, detailPrefix, loadDuplicateMatches, locale, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAccept = async () => {
    if (!window.confirm(t(`${detailPrefix}.acceptConfirm`))) {
      return;
    }
    setBusy(true);
    setErrorMessage("");
    try {
      const body = await acceptVendorDirectoryCandidate(clientViewId, candidateId, {
        displayName: candidate.rawName,
        categoryKey: acceptCategoryKey,
        municipalityId: acceptMunicipalityId || undefined,
      });
      navigate(`${serviciosBase}/proveedores/${body.vendorId}`);
    } catch (error) {
      setErrorMessage(error.message || t(`${detailPrefix}.acceptError`));
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    try {
      await rejectVendorDirectoryCandidate(clientViewId, candidateId, {
        rejectionReason: rejectReason,
      });
      await load();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDefer = async () => {
    setBusy(true);
    try {
      await deferVendorDirectoryCandidate(clientViewId, candidateId, {
        deferralReason: deferReason,
      });
      await load();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    setBusy(true);
    try {
      await restoreVendorDirectoryCandidate(clientViewId, candidateId);
      await load();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleNormalize = async () => {
    setBusy(true);
    try {
      await normalizeVendorDirectoryCandidate(clientViewId, candidateId, { rerun: true });
      await load();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleScanDuplicates = async () => {
    setBusy(true);
    try {
      await scanVendorDirectoryCandidateDuplicates(clientViewId, candidateId);
      await load();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content cvd-admin">
        <ClientPortalLoading message={t(`${detailPrefix}.loading`)} />
      </div>
    );
  }

  if (errorMessage && !candidate) {
    return (
      <div className="page-content cvd-admin">
        <ClientPortalError message={errorMessage} onRetry={load} />
      </div>
    );
  }

  const mapped = candidate?.rawPayload?.mapped || {};
  const statusLabel = candidate?.status
    ? t(`${COPY_PREFIX}.status.${candidate.status}`, {}, candidate.status)
    : "—";
  const viewName = activeView?.name || "CasaMapa";
  const categoryLabel =
    categoryByKey.get(candidate?.suggestedCategoryKey)?.name ||
    candidate?.suggestedCategoryName ||
    candidate?.rawCategory ||
    candidate?.suggestedCategoryKey ||
    "—";
  const municipalityLabel =
    municipalityById.get(candidate?.suggestedMunicipalityId)?.name ||
    candidate?.rawMunicipality ||
    "—";
  const isTerminal = candidate?.status === "accepted" || candidate?.status === "rejected";
  const hasDuplicateRisk =
    candidate?.status === "possible_duplicate" || duplicateMatches.length > 0;
  const acceptReadiness = evaluateCandidateAcceptReadiness({
    candidate,
    categoryKey: acceptCategoryKey,
    municipalityId: acceptMunicipalityId,
    categories,
  });
  const acceptBlockerMessages = acceptReadiness.blockers.map((blocker) =>
    t(`${detailPrefix}.acceptBlocker.${blocker}`)
  );

  const translateMappedField = (key) =>
    translateKey(t, `${detailPrefix}.mappedField`, key);

  return (
    <div className="page-content cvd-admin cvd-candidate-review">
      <Link className="cvd-vendor-workspace__back" to={basePath}>
        {t(`${detailPrefix}.backToList`)}
      </Link>

      <header className="cvd-candidate-review__header">
        <p className="cvd-candidate-review__eyebrow">{t(`${detailPrefix}.eyebrow`)}</p>
        <div className="cvd-admin__header">
          <div>
            <h1 className="cvd-admin__title">{candidate?.rawName || t(`${detailPrefix}.defaultTitle`)}</h1>
            <p className="lede">{t(`${detailPrefix}.intro`, { viewName })}</p>
          </div>
        </div>
        <div className="cvd-candidate-review__badges">
          <span className={`cvd-badge ${candidateStatusBadgeClass(candidate?.status)}`}>{statusLabel}</span>
          {categoryLabel !== "—" && <span className="cvd-badge cvd-badge--draft">{categoryLabel}</span>}
          {municipalityLabel !== "—" && <span className="cvd-badge cvd-badge--draft">{municipalityLabel}</span>}
        </div>
      </header>

      {errorMessage && <ClientPortalError message={errorMessage} />}

      {candidate?.status === "accepted" && (
        <StatusAlert variant="success">
          {t(`${detailPrefix}.acceptedAlert`)}{" "}
          {candidate.acceptedVendorId && (
            <Link to={`${serviciosBase}/proveedores/${candidate.acceptedVendorId}`}>
              {t(`${detailPrefix}.openVendor`)}
            </Link>
          )}
        </StatusAlert>
      )}
      {candidate?.status === "rejected" && (
        <StatusAlert variant="danger">{t(`${detailPrefix}.rejectedAlert`)}</StatusAlert>
      )}
      {candidate?.status === "deferred" && (
        <StatusAlert variant="warning">{t(`${detailPrefix}.deferredAlert`)}</StatusAlert>
      )}
      {hasDuplicateRisk && candidate?.status !== "accepted" && candidate?.status !== "rejected" && (
        <StatusAlert variant="warning">{t(`${detailPrefix}.possibleDuplicateAlert`)}</StatusAlert>
      )}

      <VendorCandidatePublicProfilePanel
        candidate={candidate}
        categoryLabel={categoryLabel}
        municipalityLabel={municipalityLabel}
        serviciosBase={serviciosBase}
      />

      {!isTerminal && (
        <section className="cvd-decision-panel" aria-labelledby="candidate-decision-title">
          <h2 id="candidate-decision-title">{t(`${detailPrefix}.decisionTitle`)}</h2>
          <p className="cvd-decision-panel__hint">{t(`${detailPrefix}.decisionHint`)}</p>
          <ol className="cvd-review-steps">
            <li className="cvd-step cvd-step--done">{t(`${detailPrefix}.stepCheckProfile`)}</li>
            <li className={`cvd-step ${duplicateMatches.length ? "cvd-step--done" : ""}`}>
              {t(`${detailPrefix}.stepCheckDuplicates`)}
            </li>
            <li className={`cvd-step ${acceptReadiness.canAccept ? "cvd-step--active" : ""}`}>
              {t(`${detailPrefix}.stepDecide`)}
            </li>
          </ol>

          <div className="cvd-accept-readiness">
            <h3>{t(`${detailPrefix}.acceptRequirementsTitle`)}</h3>
            <p className="cvd-accept-readiness__hint">{t(`${detailPrefix}.acceptRequirementsHint`)}</p>
            <ul className="cvd-accept-readiness__checklist">
              <li className={candidate?.rawName ? "cvd-accept-readiness__item--done" : "cvd-accept-readiness__item--blocked"}>
                {t(`${detailPrefix}.acceptRequirement.displayName`)}
              </li>
              <li
                className={
                  acceptCategoryKey
                    ? "cvd-accept-readiness__item--done"
                    : "cvd-accept-readiness__item--blocked"
                }
              >
                {t(`${detailPrefix}.acceptRequirement.primaryCategory`)}
              </li>
              <li
                className={
                  acceptMunicipalityId
                    ? "cvd-accept-readiness__item--done"
                    : "cvd-accept-readiness__item--optional"
                }
              >
                {t(`${detailPrefix}.acceptRequirement.municipality`)}
              </li>
            </ul>

            {!acceptReadiness.canAccept && acceptBlockerMessages.length > 0 ? (
              <StatusAlert variant="warning">
                <strong>{t(`${detailPrefix}.acceptBlockedTitle`)}</strong>
                <ul className="cvd-accept-readiness__blockers">
                  {acceptBlockerMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </StatusAlert>
            ) : null}

            <div className="cvd-accept-readiness__fields">
              <div className="cvd-field">
                <label htmlFor="accept-category-key">{t(`${detailPrefix}.acceptCategoryLabel`)}</label>
                <select
                  id="accept-category-key"
                  value={acceptCategoryKey}
                  onChange={(event) => setAcceptCategoryKey(event.target.value)}
                  aria-invalid={!acceptCategoryKey}
                >
                  <option value="">{t(`${detailPrefix}.acceptCategoryPlaceholder`)}</option>
                  {categories.map((category) => (
                    <option key={category.key} value={category.key}>
                      {category.name || category.nameEs || category.key}
                    </option>
                  ))}
                </select>
                {candidate?.rawCategory ? (
                  <p className="cvd-accept-readiness__field-note">
                    {t(`${detailPrefix}.acceptCategorySource`, { value: candidate.rawCategory })}
                  </p>
                ) : null}
              </div>
              <div className="cvd-field">
                <label htmlFor="accept-municipality-id">{t(`${detailPrefix}.acceptMunicipalityLabel`)}</label>
                <select
                  id="accept-municipality-id"
                  value={acceptMunicipalityId}
                  onChange={(event) => setAcceptMunicipalityId(event.target.value)}
                >
                  <option value="">{t(`${detailPrefix}.acceptMunicipalityPlaceholder`)}</option>
                  {municipalities.map((municipality) => (
                    <option key={municipality.id} value={municipality.id}>
                      {municipality.name}
                    </option>
                  ))}
                </select>
                {candidate?.rawMunicipality ? (
                  <p className="cvd-accept-readiness__field-note">
                    {t(`${detailPrefix}.acceptMunicipalitySource`, { value: candidate.rawMunicipality })}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="cvd-decision-panel__actions">
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CANDIDATES_ACCEPT}>
              <div className="cvd-decision-panel__action-group">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAccept}
                  disabled={busy || candidate?.status === "accepted" || !acceptReadiness.canAccept}
                  title={
                    !acceptReadiness.canAccept
                      ? acceptBlockerMessages.join(" ")
                      : undefined
                  }
                >
                  {t(`${detailPrefix}.acceptDraft`)}
                </button>
                <p className="cvd-decision-panel__action-hint">
                  {!acceptReadiness.canAccept
                    ? t(`${detailPrefix}.acceptDisabledHint`)
                    : t(`${detailPrefix}.acceptHint`)}
                </p>
              </div>
            </VendorDirectoryPermissionGate>
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CANDIDATES_REJECT}>
              <div className="cvd-decision-panel__action-group cvd-decision-panel__action-group--secondary">
                <select
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  aria-label={t(`${detailPrefix}.rejectReasonLabel`)}
                >
                  {REJECT_REASONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`${detailPrefix}.rejectReason.${value}`)}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-secondary" onClick={handleReject} disabled={busy}>
                  {t(`${detailPrefix}.reject`)}
                </button>
              </div>
            </VendorDirectoryPermissionGate>
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CANDIDATES_DEFER}>
              <div className="cvd-decision-panel__action-group cvd-decision-panel__action-group--secondary">
                <select
                  value={deferReason}
                  onChange={(e) => setDeferReason(e.target.value)}
                  aria-label={t(`${detailPrefix}.deferReasonLabel`)}
                >
                  {DEFER_REASONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`${detailPrefix}.deferReason.${value}`)}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-secondary" onClick={handleDefer} disabled={busy}>
                  {t(`${detailPrefix}.defer`)}
                </button>
              </div>
            </VendorDirectoryPermissionGate>
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CANDIDATES_UPDATE}>
              <button type="button" className="btn btn-secondary" onClick={handleRestore} disabled={busy}>
                {t(`${detailPrefix}.restore`)}
              </button>
            </VendorDirectoryPermissionGate>
          </div>
        </section>
      )}

      <VendorCandidateReviewContextPanels
        t={t}
        locale={locale}
        candidate={candidate}
        candidateId={candidateId}
        duplicateMatches={duplicateMatches}
        mapped={mapped}
        serviciosBase={serviciosBase}
        busy={busy}
        hasDuplicateRisk={hasDuplicateRisk}
        onScanDuplicates={handleScanDuplicates}
        onNormalize={handleNormalize}
        translateMappedField={translateMappedField}
        formatCandidateDate={formatCandidateDate}
      />
    </div>
  );
}
